import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Australia/Brisbane timezone for pilot deployment
// FE-Q3 — exported so a screen that keeps its OWN formatter still pins the
// SAME zone. Two surfaces printing one instant in two zones disagree about
// a fact of the record; a second copy of this string is how that starts.
export const TIMEZONE = 'Australia/Brisbane';

/**
 * Resolve a file URL - handles both signed R2 URLs (full https://) and local paths (/api/uploads/...).
 * Uses same-origin relative paths so deploys work on any domain.
 */
export function fileUrl(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return path;
}

export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-AU', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Alias functions for compatibility
export const fmtDate = formatDate;
export const fmtDateTime = formatDateTime;

export function fmtTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-AU', {
    timeZone: TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function fmtDateShort(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    timeZone: TIMEZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * "Mon 5 Oct 2026" — and with `time`, "Mon 5 Oct 2026, 09:00" (24 h) — of an
 * instant on the project's clock, day and hour read in the same zone. '' when
 * the value is not a date.
 *
 * DATES-1 — every date the app shows carries the year, also in the current
 * year (the dossier PDF's `format_day` / `format_instant`). A calendar date
 * (`YYYY-MM-DD`) is a date, never an instant: no zone moves it, and with
 * `time` it reads as the day alone.
 *
 * FIX-FE-1 — the zone is the second argument when the caller was served one
 * (`project_timezone`): `fmtShortDay(value, tz, { time })`. Without it —
 * `fmtShortDay(value)`, `fmtShortDay(value, { time })` — the clock is
 * `TIMEZONE`, as before. A zone the runtime does not know reads as `TIMEZONE`.
 */
export function fmtShortDay(value, zoneOrOptions, options) {
  if (!value) return '';
  const calendar = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
  const date = value instanceof Date ? value
    : new Date(calendar ? `${value.trim()}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return '';
  const zone = typeof zoneOrOptions === 'string' ? zoneOrOptions.trim() : '';
  const { time = false } = (zoneOrOptions && typeof zoneOrOptions === 'object' ? zoneOrOptions : options) || {};
  const read = (timeZone) => {
    const out = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date).forEach((p) => { out[p.type] = p.value; });
    return out;
  };
  let parts;
  if (calendar) {
    parts = read('UTC');
  } else {
    try {
      parts = read(zone || TIMEZONE);
    } catch {
      parts = read(TIMEZONE);
    }
  }
  const day = `${parts.weekday} ${parts.day} ${parts.month} ${parts.year}`;
  return time && !calendar ? `${day}, ${parts.hour}:${parts.minute}` : day;
}

/** How far `timeZone`'s wall clock is ahead of UTC at the instant `ms` (in ms). */
const zoneOffsetAt = (ms, timeZone) => {
  const p = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms)).forEach((part) => { p[part.type] = part.value; });
  const wall = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(p.hour), Number(p.minute), Number(p.second));
  return wall - Math.floor(ms / 1000) * 1000;
};

/**
 * FIX-FE-2 (B3) — `YYYY-MM-DD` + `HH:MM` read on the wall clock of `tz`, as
 * the instant (a `Date`) they name. PURE; no dependency (`Intl` only).
 *
 * `null` — never a made-up instant — when there is no zone, the runtime does
 * not know it, or the two strings are not a real date and time.
 *
 * The two edges of daylight saving answer as the server does for the same
 * strings (`handover_local_instant`: pytz `localize`, standard time):
 *   - a wall time the clock jumps over (02:30 on the day it starts) is read
 *     on standard time, so it lands one hour later on the new clock;
 *   - a wall time that happens twice (02:30 on the day it ends) is the second.
 */
export function wallClockToInstant(dateStr, timeStr, tz) {
  const zone = typeof tz === 'string' ? tz.trim() : '';
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof dateStr === 'string' ? dateStr.trim() : '');
  const t = /^(\d{2}):(\d{2})(:\d{2})?$/.exec(typeof timeStr === 'string' ? timeStr.trim() : '');
  if (!zone || !d || !t) return null;
  const [year, month, day, hour, minute] = [d[1], d[2], d[3], t[1], t[2]].map(Number);
  if (hour > 23 || minute > 59) return null;
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  const back = new Date(wall);
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) return null;
  const DAY = 24 * 3600 * 1000;
  try {
    const before = zoneOffsetAt(wall - DAY, zone);
    const after = zoneOffsetAt(wall + DAY, zone);
    if (before === after) return new Date(wall - before);
    const first = wall - before;
    const second = wall - after;
    const firstHolds = zoneOffsetAt(first, zone) === before;
    const secondHolds = zoneOffsetAt(second, zone) === after;
    if (firstHolds && !secondHolds) return new Date(first);
    if (secondHolds && !firstHolds) return new Date(second);
    // Jumped over, or twice: standard time (the smaller offset).
    return new Date(wall - Math.min(before, after));
  } catch {
    return null;
  }
}

export function getCurrentDateInTimezone() {
  // Get current date in Australia/Brisbane timezone (YYYY-MM-DD format)
  const now = new Date();
  return now.toLocaleDateString('en-CA', { timeZone: TIMEZONE });
}

export function getCurrentTimeInTimezone() {
  // Get current time in Australia/Brisbane timezone (HH:MM format)
  const now = new Date();
  return now.toLocaleTimeString('en-AU', { 
    timeZone: TIMEZONE,
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
}

export function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case 'ok':
      return 'text-green-500 bg-green-500/10 border-green-500/30';
    case 'nok':
      return 'text-red-500 bg-red-500/10 border-red-500/30';
    case 'na':
      return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
    case 'open':
      return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/30';
    case 'in_review':
    case 'in_progress':
      return 'text-blue-500 bg-blue-500/10 border-blue-500/30';
    case 'closed':
      return 'text-green-500 bg-green-500/10 border-green-500/30';
    default:
      return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
  }
}

export function getPinColor(status) {
  switch (status?.toLowerCase()) {
    case 'ok':
    case 'closed':
      return 'bg-green-600 ring-green-900';
    case 'nok':
    case 'open':
      return 'bg-red-600 ring-red-900 animate-pulse';
    default:
      return 'bg-slate-600 ring-slate-900';
  }
}

export function getElementTypeLabel(type) {
  const labels = {
    footing: 'Footing',
    pile_cap: 'Pile Cap',
    slab: 'Slab',
    column: 'Column',
    beam: 'Beam',
    drainage_pit: 'Drainage Pit',
    drainage_pipe: 'Drainage Pipe',
    culvert: 'Culvert',
    headwall: 'Headwall',
    scour_protection: 'Scour Protection',
    drainage_channel: 'Drainage Channel',
    agi_line: 'AGI Line',
    other: 'Other',
    subgrade: 'Subgrade',
    subbase: 'Subbase',
    basecourse: 'Basecourse',
    asphalt: 'Asphalt',
    concrete_pavement: 'Concrete Pavement',
    kerb: 'Kerb',
    cable_trench: 'Cable Trench',
    conduit: 'Conduit',
    electrical_pit: 'Electrical Pit',
    earthworks_general: 'Earthworks (General)',
    excavation: 'Excavation',
    fill: 'Fill',
    compaction: 'Compaction',
    utilities_general: 'Utilities (General)',
    water_main: 'Water Main',
    sewer: 'Sewer',
    gas: 'Gas',
    telecom: 'Telecom',
  };
  return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// CO-3: Display formatter for ProjectCompany.type
export const formatCompanyType = (type) => {
  if (!type) return '';
  const map = {
    main_contractor: 'Main Contractor',
    subcontractor: 'Subcontractor',
    client: 'Client',
    consultant: 'Consultant',
    supplier: 'Supplier',
    other: 'Other',
  };
  return map[type.toLowerCase()] || type;
};

// CO-3: Available types for dropdowns (mirrors backend ProjectCompanyType enum)
export const PROJECT_COMPANY_TYPES = [
  { value: 'main_contractor', label: 'Main Contractor' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'client', label: 'Client' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'other', label: 'Other' },
];

/**
 * LC17/4 — what a Client Representative actually does, in one sentence, defined
 * ONCE.
 *
 * The old copy was "External client — read access + handover actions". Both
 * halves under-sold it: "read access" reads like Viewer, and "handover actions"
 * names a screen rather than an act. The role is the only one that can accept
 * an asset (Portao 2, docs/design/asset-lifecycle.md §5), and it is the client
 * side of the punch-item loop — it raises them and it verifies the fix. An
 * admin choosing a role for an external client needs to know that before they
 * choose, not after.
 *
 * A constant rather than a literal because the invite modal renders one of two
 * role lists depending on mode (Company vs Project), and a sentence that
 * describes a role must not depend on which list is on screen.
 */
// RENAME-HR (ratified Cléber 2026-08-23): the role's human label is
// "Handover Reviewer" (slug `client_representative` unchanged, permission
// matrix unchanged). The sentence is the ratified description, verbatim. It no
// longer claims the role "accepts assets" — acceptance is DERIVED from the
// handover inspection record (the old accept door is 410).
export const CLIENT_REPRESENTATIVE_DESCRIPTION =
  'Reviews handover on behalf of the receiving company. Can view project '
  + 'quality records, raise punch items, and verify or reject corrective '
  + 'actions. Cannot administer the project, assign responsibilities, or sign '
  + 'ITCs solely because of this role.';

export function getRoleLabel(role) {
  const labels = {
    platform_owner:   'Platform Owner',
    company_qa_admin: 'Company QA Admin',
    // NL21-A10 — the project role above Admin; the server's ROLE_LABELS.
    company_representative: 'Company Representative',
    admin:            'Admin',
    operator:         'Operator',
    viewer:           'Viewer',
    // LC9 — the external project role. Same wording as the server's
    // ROLE_LABELS, which is what the invitation email says (RENAME-HR).
    client_representative: 'Handover Reviewer',
    // legacy fallbacks (pre-migration data)
    project_admin:    'Admin',
    inspector:        'Operator',
    subcontractor:    'Operator',
  };
  return labels[role] || role;
}

export function truncate(str, length = 50) {
  if (!str) return '';
  return str.length > length ? str.substring(0, length) + '...' : str;
}
